/**
 * Lightweight, deterministic emphasis heuristics.
 *
 * Rendering never *depends* on emphasis - this only suggests words to highlight.
 * Users can accept, reject or set emphasis manually. An AI provider can later
 * implement the same `EmphasisSuggester` interface.
 */
const STRONG_WORDS = new Set([
    'never',
    'always',
    'best',
    'worst',
    'free',
    'now',
    'stop',
    'must',
    'huge',
    'massive',
    'secret',
    'proven',
    'guaranteed',
    'instantly',
    'exactly',
    'critical',
    'important',
]);
const CURRENCY = /[$£€₹¥]/;
const PERCENT = /\d+(\.\d+)?%/;
const NUMBERISH = /\d[\d,.]*/;
export function suggestEmphasis(words) {
    const out = [];
    for (const w of words) {
        const raw = w.text;
        const clean = raw.toLowerCase().replace(/[^\p{L}]/gu, '');
        if (CURRENCY.test(raw))
            out.push({ wordId: w.id, reason: 'currency' });
        else if (PERCENT.test(raw))
            out.push({ wordId: w.id, reason: 'percentage' });
        else if (NUMBERISH.test(raw))
            out.push({ wordId: w.id, reason: 'number' });
        else if (STRONG_WORDS.has(clean))
            out.push({ wordId: w.id, reason: 'strong-word' });
        else if (raw.length >= 3 && raw === raw.toUpperCase() && /\p{L}/u.test(raw))
            out.push({ wordId: w.id, reason: 'all-caps' });
    }
    return out;
}
export function applyEmphasisSuggestions(words, suggestions) {
    const ids = new Set(suggestions.map((s) => s.wordId));
    return words.map((w) => (ids.has(w.id) ? { ...w, emphasized: true } : w));
}
/**
 * Enforce "at most one emphasized word per caption window": within each segment,
 * keep the FIRST emphasized word (reading order) and clear the rest. Segments
 * can be re-grouped (e.g. switching template) so two separately-chosen key words
 * may land in one on-screen window - this keeps exactly one popped word there.
 * Words not belonging to any segment are left untouched. Returns the same array
 * reference when nothing changed (cheap no-op for unrelated edits).
 */
export function enforceSingleEmphasisPerSegment(words, segments) {
    const emphasizedIds = new Set(words.filter((w) => w.emphasized).map((w) => w.id));
    if (emphasizedIds.size === 0)
        return words;
    const toClear = new Set();
    for (const seg of segments) {
        let kept = false;
        for (const id of seg.wordIds) {
            if (!emphasizedIds.has(id))
                continue;
            if (kept)
                toClear.add(id);
            else
                kept = true;
        }
    }
    if (toClear.size === 0)
        return words;
    return words.map((w) => (toClear.has(w.id) ? { ...w, emphasized: false } : w));
}
//# sourceMappingURL=emphasis.js.map